/* =========================================================
   src/app.jsx - root + mount
   ========================================================= */

import ReactDOM from "react-dom/client";
import React from "react";

function App() {
  const isMobile = useMediaQuery("(max-width: 720px)");

  useDeckEngine(!isMobile);
  useSnapController(!isMobile);
  useScrollToTopOnMount();
  useMobileFadeReveal(isMobile);

  const slides = [
    { id: "hero",     bg: "paper",  name: "01 Hero",     content: <Hero /> },
    { id: "services", bg: "paper2", name: "02 Services", content: <Services /> },
    { id: "values",   bg: "ink",    name: "03 Values",   content: <Values /> },
    { id: "contact",  bg: "ink",    name: "04 Contact",  content: <Contact /> },
  ];

  return (
    <>
      <BackgroundLayer />
      <CursorGlow />
      <ScrollProgressBar />
      <Header />
      {isMobile ? <MobilePage /> : <Deck slides={slides} />}
    </>
  );
}

function MobilePage() {
  const pages = React.useMemo(() => [
    { id: "hero", content: <MobileHero /> },
    { id: "services", content: <MobileServices /> },
    { id: "values", content: <MobileValues /> },
    { id: "contact", content: <MobileContact /> },
  ], []);

  const goToPage = React.useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navOffset = 72;
    const targetY = el.getBoundingClientRect().top + window.scrollY - navOffset;
    animateScrollTo(Math.max(0, targetY), 520);
  }, [pages]);

  React.useEffect(() => {
    window.__deckGoTo = goToPage;
    return () => { delete window.__deckGoTo; };
  }, [goToPage]);

  return (
    <main className="mobile-page" aria-label="Morse and Code mobile site">
      {pages.map((page) => (
        <div
          key={page.id}
          id={page.id}
          className={cx(
            "mobile-page-panel",
            "mobile-reveal-section",
            page.id === "hero" && "mobile-hero-section"
          )}
        >
          {page.content}
        </div>
      ))}
    </main>
  );
}

const MOBILE_SERVICES = [
  ["LineChart", "Consultancy", "Focused digital strategy for high-pressure business decisions."],
  ["RefreshCcw", "Outsourcing", "Senior specialists who can step into critical roles quickly."],
  ["MonitorSmartphone", "Transformation", "Connected systems that replace slow, fragmented operations."],
  ["LayoutGrid", "Architecture", "Secure foundations built for scale, speed, and stability."],
  ["Gauge", "Optimization", "Cleaner workflows with less friction and measurable results."],
];

const MOBILE_VALUES = [
  ["Reliability", "We do what we say and stay reachable when things get complicated."],
  ["Responsibility", "Deadlines, budgets, security, and consequences are treated seriously."],
  ["Human Approach", "Every process and dashboard exists for actual people doing work."],
  ["Professionalism", "Sharp thinking, clean execution, and calm delivery."],
  ["Innovation", "Useful improvements that save time and remove friction."],
];

function MobileHero() {
  return (
    <section className="m-hero">
      <div className="m-kicker">Morse &amp; Code</div>
      <h1>Digital change, made usable.</h1>
      <p>
        We help companies turn complex processes into clear, secure, practical digital systems.
      </p>
      <div className="m-actions">
        <Button size="lg" onClick={() => smoothScrollTo("contact")}>
          Contact us
          <Icon name="ArrowRight" size={16} />
        </Button>
        <Button variant="ghost" size="lg" onClick={() => smoothScrollTo("services")}>
          Services
        </Button>
      </div>
      <div className="m-proof">
        <span><strong>10+</strong> years</span>
        <span><strong>40</strong> projects</span>
        <span><strong>EU</strong> based</span>
      </div>
    </section>
  );
}

function MobileServices() {
  return (
    <section className="m-section">
      <MobileSectionHead label="Services" title="What we solve." />
      <div className="m-service-list">
        {MOBILE_SERVICES.map(([icon, title, body], index) => (
          <article className="m-service" key={title}>
            <div className="m-service-icon"><Icon name={icon} size={18} strokeWidth={1.8} /></div>
            <div>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <h2>{title}</h2>
              <p>{body}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function MobileValues() {
  return (
    <section className="m-section">
      <MobileSectionHead label="Values" title="How we work." />
      <div className="m-value-list">
        {MOBILE_VALUES.map(([title, body], index) => (
          <article className="m-value" key={title}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div>
              <h2>{title}</h2>
              <p>{body}</p>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

function MobileContact() {
  const [values, setValues] = React.useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const update = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!values.name.trim()) errs.name = "Please enter your name.";
    if (!values.email.trim()) errs.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(values.email)) errs.email = "That doesn’t look like a valid email.";
    if (!values.message.trim()) errs.message = "Tell us a little about your project.";
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      setValues({ name: "", email: "", message: "" });
    }, 700);
  };

  return (
    <section className="m-section m-contact">
      <MobileSectionHead label="Contact" title="Let’s talk." />
      <p className="m-contact-lede">
        Tell us what you are building. We usually reply within one business day.
      </p>

      <form className="m-contact-form" onSubmit={onSubmit} noValidate>
        <Field id="mc-name" label="Your name" error={errors.name}>
          <Input id="mc-name" placeholder="Jane Doe" autoComplete="name"
            value={values.name} onChange={update("name")} aria-invalid={!!errors.name} />
        </Field>
        <Field id="mc-email" label="Email" error={errors.email}>
          <Input id="mc-email" type="email" placeholder="jane@company.com" autoComplete="email"
            value={values.email} onChange={update("email")} aria-invalid={!!errors.email} />
        </Field>
        <Field id="mc-message" label="How can we help?" error={errors.message}>
          <Textarea id="mc-message" placeholder="A short description of what you’re working on…"
            value={values.message} onChange={update("message")} aria-invalid={!!errors.message} />
        </Field>
        <Button size="lg" type="submit" disabled={submitting}>
          {submitting ? (
            <><Icon name="Loader" size={14} className="spin" /> Sending…</>
          ) : (
            <>Send message <Icon name="ArrowRight" size={14} className="btn-arrow" /></>
          )}
        </Button>
        {done && (
          <Alert>
            Thanks — we’ve received your message. We’ll reply within one business day.
          </Alert>
        )}
      </form>

      <div className="m-contact-card">
        <a href="mailto:info@morsecode.cz">
          <Icon name="Mail" size={16} />
          info@morsecode.cz
        </a>
        <a href="https://morsecodesro.youtrack.cloud/" target="_blank" rel="noopener noreferrer">
          <Icon name="LifeBuoy" size={16} />
          Helpdesk
        </a>
        <div>
          <Icon name="MapPin" size={16} />
          Spálená 480/1, 602 00 Brno-Trnitá
        </div>
      </div>

      <div className="m-registry">
        <strong>Morse &amp; Code s.r.o.</strong>
        <span>IČ: 21426937 · DIČ: CZ21426937</span>
        <span>Krajský soud v Brně, oddíl C, vložka 138867</span>
        <span>ID datové schránky: 2tm7mws</span>
      </div>
    </section>
  );
}

function MobileSectionHead({ label, title }) {
  return (
    <header className="m-section-head">
      <span>{label}</span>
      <h1>{title}</h1>
    </header>
  );
}

function useMobileFadeReveal(enabled) {
  React.useEffect(() => {
    const sections = document.querySelectorAll(".mobile-reveal-section");

    if (!enabled || isReducedMotion()) {
      sections.forEach(el => el.classList.add("is-visible"));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.08 }
    );

    sections.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [enabled]);
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
